/**
 * The tooltip system (SHELL_CHROME_SPEC §13) — ONE positioning/dismissal
 * engine, two tiers:
 *
 *  - Tier 1, the tip: a few words. Hover 300ms / focus 0ms; the tip TEXT also
 *    lives in the DOM as a visually-hidden sibling the control references via
 *    `aria-describedby` statically, so AT reads it without the visual layer.
 *    After one tip has shown, siblings show with no delay for 500ms (the
 *    toolbar-warmth idiom every DCC uses).
 *  - Tier 2, the explanation: the paragraph tier behind an always-visible ?
 *    button (`aria-expanded`; a door keyboard and touch can find must not
 *    require a pointer to discover). Escape returns focus to the button.
 *    Content is read through a thunk at open time — a tier-2 that quotes live
 *    state (the measured baseline) must never go stale.
 *
 * No `title=` attributes anywhere (§13.3) — native tooltips are unstyled,
 * delay-uncontrollable, and invisible to touch/keyboard.
 * Depends on: DOM only. Depended on by: value-row, panels.
 */

let tier1El: HTMLDivElement | null = null;
let tier2El: HTMLDivElement | null = null;
let tier2Owner: HTMLButtonElement | null = null;
let warmUntil = 0;
let tipSeq = 0;

function tier1(): HTMLDivElement {
  if (tier1El === null) {
    tier1El = document.createElement('div');
    tier1El.id = 'tier1-tip';
    tier1El.setAttribute('role', 'presentation'); // AT reads the hidden sibling, not this
    document.body.appendChild(tier1El);
  }
  return tier1El;
}

function tier2(): HTMLDivElement {
  if (tier2El === null) {
    tier2El = document.createElement('div');
    tier2El.id = 'tier2-pop';
    tier2El.setAttribute('role', 'tooltip'); // non-interactive text only; a link inside graduates it to dialog (§13.2)
    document.body.appendChild(tier2El);
    // Singleton listeners — attached ONCE with the element, never per button:
    // panels remount on every layout change, and per-button document
    // listeners would accumulate across dispose cycles.
    document.addEventListener('pointerdown', (e) => {
      if (tier2Owner !== null && e.target instanceof Node && !tier2El!.contains(e.target) && e.target !== tier2Owner) {
        closeTier2();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && tier2Owner !== null) {
        const owner = tier2Owner;
        closeTier2();
        owner.focus(); // §13.2: Escape returns focus to the ?
      }
    });
  }
  return tier2El;
}

/** Above the anchor, 6px gap; flips below at the viewport's top edge. */
function place(el: HTMLElement, anchor: HTMLElement): void {
  const a = anchor.getBoundingClientRect();
  el.style.left = '0px';
  el.style.top = '0px';
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  let left = a.left + a.width / 2 - w / 2;
  left = Math.max(4, Math.min(left, window.innerWidth - w - 4));
  let top = a.top - h - 6;
  if (top < 4) top = a.bottom + 6;
  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
}

/**
 * Close the tier-2 popover whoever owns it. Exported as the panel-teardown
 * hook (Codex P2, #78): the popover is a body-level singleton whose owner
 * button lives inside a panel, so a layout remount detaches the owner and
 * strands the popover — still `data-open="true"` at stale coordinates, with
 * Escape trying to focus a disconnected button. The workspace shell calls
 * this from its disposal seam whenever panels unmount.
 */
export function closeTier2(): void {
  if (tier2El !== null) tier2El.dataset['open'] = 'false';
  if (tier2Owner !== null) {
    tier2Owner.setAttribute('aria-expanded', 'false');
    // The singleton's content belongs to whoever opens it NEXT (Codex P2,
    // #78): a stale describedby would have AT announce another row's
    // explanation from this button. The ? button carries no other
    // description, so removal is total.
    tier2Owner.removeAttribute('aria-describedby');
  }
  tier2Owner = null;
}

/**
 * Wire a tier-1 tip onto a control. Returns the hidden describedby node so a
 * caller composing rows can place it (appended to the control's parent when
 * that is not supplied elsewhere).
 */
export function attachTip(control: HTMLElement, text: string): HTMLElement {
  const hidden = document.createElement('span');
  hidden.className = 'tip-anchor-text';
  tipSeq += 1;
  hidden.id = `tip-text-${tipSeq}`;
  hidden.textContent = text;
  const existing = control.getAttribute('aria-describedby');
  control.setAttribute('aria-describedby', existing === null ? hidden.id : `${existing} ${hidden.id}`);

  let timer: ReturnType<typeof setTimeout> | null = null;
  const show = (): void => {
    const el = tier1();
    el.textContent = text;
    el.dataset['open'] = 'true';
    place(el, control);
  };
  const hide = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const el = tier1();
    if (el.dataset['open'] === 'true') warmUntil = Date.now() + 500;
    el.dataset['open'] = 'false';
  };
  control.addEventListener('pointerenter', () => {
    const delay = Date.now() < warmUntil ? 0 : 300;
    timer = setTimeout(show, delay);
  });
  control.addEventListener('pointerleave', hide);
  control.addEventListener('focus', show);
  control.addEventListener('blur', hide);
  control.addEventListener('pointerdown', hide);
  // Escape dismisses a focus-shown tip without surrendering focus (Codex P2,
  // #78 — §13.1 lists Escape; the tier-2 document handler only acts while a
  // popover owner exists). On the control, not document: the listener's
  // lifetime is the control's, so panel remounts leak nothing.
  control.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  return hidden;
}

/**
 * attachTip for a control about to be appended: the control and its hidden
 * describedby text as a pair, so `parent.append(...withTip(b, 'words'))` parks
 * the text beside the control in one line. Born with the Body-tab
 * simplification (XD 2026-09-29 §4): the four Body panels carried 28 `title=`
 * attributes (§13.3 says none), each built inline where the parent was not
 * yet known. The hidden node is position:absolute, so a flex row is unmoved.
 */
export function withTip<T extends HTMLElement>(control: T, text: string): [T, HTMLElement] {
  return [control, attachTip(control, text)];
}

/**
 * The tier-2 door: an always-visible circled-? button. `content` is read at
 * open time; values inside may be marked up by the caller via `renderInto`
 * convention: plain text with `**value**` runs becoming mono accent spans.
 */
export function describeButton(label: string, content: () => string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'describe-btn';
  btn.textContent = '?';
  btn.setAttribute('aria-label', `about ${label}`);
  btn.setAttribute('aria-expanded', 'false');

  const open = (): void => {
    const el = tier2();
    if (tier2Owner === btn) {
      closeTier2();
      return;
    }
    closeTier2();
    el.replaceChildren(
      ...content()
        .split('**')
        .map((run, i) => {
          if (i % 2 === 0) return document.createTextNode(run);
          const v = document.createElement('span');
          v.className = 'tip-value';
          v.textContent = run;
          return v;
        }),
    );
    el.dataset['open'] = 'true';
    btn.setAttribute('aria-describedby', el.id);
    btn.setAttribute('aria-expanded', 'true');
    tier2Owner = btn;
    place(el, btn);
  };
  let hoverTimer: ReturnType<typeof setTimeout> | null = null;
  btn.addEventListener('click', () => {
    // The click owns the toggle: a still-armed hover timer (enter → click-open
    // → click-close, all inside the 1000ms) would fire and reopen what the
    // user just dismissed (Codex P2, #78).
    if (hoverTimer !== null) clearTimeout(hoverTimer);
    hoverTimer = null;
    open();
  });
  btn.addEventListener('pointerenter', () => {
    hoverTimer = setTimeout(() => {
      if (tier2Owner !== btn) open();
    }, 1000);
  });
  btn.addEventListener('pointerleave', () => {
    if (hoverTimer !== null) clearTimeout(hoverTimer);
  });
  return btn;
}
